/**
 * Handing items to the kitchen, and taking them back.
 *
 * Somebody from the kitchen walks in and says what they need; the store
 * keeper picks the items here, types the packs handed over, and presses
 * Issue. The stock moves from the Store shelf to the Kitchen shelf and the
 * slip prints — one A4 page, two halves, both signed, cut across the line.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTime, today } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { ItemPicker, packs } from './ItemPicker.jsx';
import { IssueSlipSheet } from './IssueSlip.jsx';
import { DateFilter, useDateFilter } from './DateFilter.jsx';

const EMPTY = { direction: 'to_kitchen', note: '', lines: [] };

export function IssuesTab({ items }) {
  const qc = useQueryClient();
  const dates = useDateFilter();
  const issues = useQuery({ queryKey: ['store', 'issues', dates.query], queryFn: () => api.get(`/store/issues?limit=${dates.range ? 500 : 50}${dates.query}`) });
  const [draft, setDraft] = useState(EMPTY);
  // The day it was handed over: today, unless yesterday's slips are being written up late.
  const [date, setDate] = useState(today());
  const [printing, setPrinting] = useState(null);
  const byId = new Map(items.map((i) => [i.id, i]));
  const toKitchen = draft.direction === 'to_kitchen';

  const issue = useMutation({
    mutationFn: () => api.post('/store/issues', { direction: draft.direction, note: draft.note, date, lines: draft.lines.map((l) => ({ ingredientId: l.ingredientId, packs: Number(l.packs) })) }),
    onSuccess: (res) => {
      setDraft({ ...EMPTY, direction: draft.direction });
      setDate(today());
      qc.invalidateQueries({ queryKey: ['store'] });
      setPrinting(res.id);
    },
  });

  const edit = (i, patch) => setDraft({ ...draft, lines: draft.lines.map((l, k) => (k === i ? { ...l, ...patch } : l)) });
  const ready = draft.lines.length > 0 && draft.lines.every((l) => Number(l.packs) > 0);

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <section className="rounded-xl border border-rule bg-surface p-4 lg:col-span-3">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-ink">{toKitchen ? 'Issue to kitchen' : 'Return to store'}</h2>
          <div role="tablist" className="flex rounded-lg border border-rule p-0.5">
            {[
              ['to_kitchen', 'To kitchen'],
              ['to_store', 'Back to store'],
            ].map(([d, label]) => (
              <button key={d} role="tab" aria-selected={draft.direction === d} onClick={() => setDraft({ ...draft, direction: d })} className={`rounded-md px-3 py-1.5 text-xs font-medium ${draft.direction === d ? 'bg-brand-500 text-black' : 'text-ink-2'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="mb-3 text-xs text-ink-3">Pick what is being handed over and type the packs. Half packs are fine. The slip prints when you press the button.</p>
        <label className="mb-3 block text-xs text-ink-3 sm:w-56">
          {toKitchen ? 'Issued on' : 'Returned on'}
          <input type="date" value={date} max={today()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label={toKitchen ? 'Issued on' : 'Returned on'} className="mt-1 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
        </label>
        <ItemPicker items={items} onPick={(it) => !draft.lines.some((l) => l.ingredientId === it.id) && setDraft({ ...draft, lines: [...draft.lines, { ingredientId: it.id, name: it.name, packName: it.packName, packSize: it.packSize, unit: it.unit, packs: '1' }] })} placeholder="Add an item…" autoFocus />
        {draft.lines.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-3">
                <th className="py-1 font-medium">Item</th>
                <th className="py-1 text-right font-medium">{toKitchen ? 'In store' : 'In kitchen'}</th>
                <th className="py-1 text-right font-medium">Packs</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {draft.lines.map((l, i) => {
                const it = byId.get(l.ingredientId);
                const shelf = it ? (toKitchen ? it.store.packs : it.kitchen.packs) : null;
                return (
                  <tr key={l.ingredientId} className="border-t border-rule">
                    <td className="py-2 text-ink">
                      {l.name}
                      <div className="text-xs text-ink-3">
                        {l.packName}
                        {l.packSize !== 1 ? ` = ${packs(l.packSize)} ${l.unit}` : ''}
                      </div>
                    </td>
                    <td className={`py-2 text-right tabular-nums ${shelf != null && Number(l.packs) > shelf ? 'text-warn' : 'text-ink-3'}`}>{shelf != null ? packs(shelf) : '—'}</td>
                    <td className="py-2 text-right">
                      <input type="number" step="any" min="0" value={l.packs} onChange={(e) => edit(i, { packs: e.target.value })} aria-label={`Packs of ${l.name}`} className="h-10 w-20 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm text-ink" />
                    </td>
                    <td className="py-2 text-right">
                      <button type="button" onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, k) => k !== i) })} aria-label={`Remove ${l.name}`} className="text-xs text-ink-3 hover:text-bad">
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Note (optional) — who took it, for which shift…" className="mt-3 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
        {issue.error && (
          <p role="alert" className="mt-2 text-sm text-bad">
            {issue.error.message}
          </p>
        )}
        <div className="mt-3 flex justify-end gap-2">
          {draft.lines.length > 0 && (
            <Button variant="secondary" onClick={() => setDraft({ ...EMPTY, direction: draft.direction })}>
              Clear
            </Button>
          )}
          <Button onClick={() => issue.mutate()} disabled={!ready} busy={issue.isPending}>
            {toKitchen ? 'Issue and print slip' : 'Return and print slip'}
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-rule bg-surface p-4 lg:col-span-2">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-ink-2">{dates.range ? 'Slips on those days' : 'Slips'}</h2>
          <DateFilter range={dates.range} onChange={dates.setRange} />
        </div>
        {dates.range && issues.data && (
          <p className="mb-2 text-xs text-ink-3">
            {issues.data.issues.length} slip{issues.data.issues.length === 1 ? '' : 's'} · to kitchen {formatPKR(issues.data.issues.filter((s) => s.to_purpose === 'kitchen').reduce((t, s) => t + (Number(s.total_value) || 0), 0))}
          </p>
        )}
        <ul className="space-y-1.5 text-sm">
          {(issues.data?.issues ?? []).map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-rule px-3 py-2">
              <span>
                <span className="text-ink">{s.transfer_number}</span>
                <span className="text-ink-3">
                  {' '}
                  · {s.to_purpose === 'kitchen' ? 'to kitchen' : 'back to store'} · {s.lines} item{s.lines === 1 ? '' : 's'} · {formatPKR(s.total_value)}
                </span>
                <div className="text-xs text-ink-3">
                  {formatServerTime(s.created_at)} · {s.issued_by}
                  {s.notes ? ` · ${s.notes}` : ''}
                </div>
              </span>
              <Button size="sm" variant="secondary" onClick={() => setPrinting(s.id)}>
                Print
              </Button>
            </li>
          ))}
          {issues.data && issues.data.issues.length === 0 && <li className="text-ink-3">{dates.range ? 'No slips on those days.' : 'No slips yet.'}</li>}
        </ul>
      </section>
      {printing && <IssueSlipSheet issueId={printing} onClose={() => setPrinting(null)} />}
    </div>
  );
}
